'use client';

import React, { useState } from 'react';
import { chakra } from '@chakra-ui/react';

import getEnvironmentVariables from '@/server/utils/getEnvironmentVars';
import { trpc } from '@/trpc';
import usePartyNumber from '@/hooks/usePartyNumber';

import { useChatSchedule } from './hooks/useChatSchedule';
import { useSalesforceMessaging } from './hooks/useSalesforceMessaging';

export type LiveChatWorkHours = {
  hourOpen: number;
  hourClosed: number;
};

interface ChatWidgetCoreProps {
  partyNumber: string;
  displayName: string;
  workhours: LiveChatWorkHours;
  envVars: Record<string, string>;
}

const ChatButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <chakra.button
      type="button"
      position="fixed"
      zIndex={9999}
      bottom={6}
      right={6}
      height={12}
      px={4}
      alignItems="center"
      display="flex"
      background="#0070D2"
      borderRadius="full"
      boxShadow="card.light"
      color="white"
      fontWeight="bold"
      onClick={onClick}
    >
      <chakra.div
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="54px"
        height="54px"
        borderWidth="3px"
        borderColor="#0070D2"
        ml={-4}
        mr={2}
        borderRadius="full"
        position="relative"
        background="white"
      >
        <chakra.svg
          width="36px"
          height="36px"
          viewBox="0 0 52 52"
          xmlns="http://www.w3.org/2000/svg"
          display="block"
          position="relative"
          zIndex={2}
        >
          <path
            d="M26 8C15.4 8 7 15.3 7 24.4c0 4.8 2.4 9.1 6.4 12.2v7.4l7.1-3.9c1.8.5 3.7.8 5.5.8 10.6 0 19-7.3 19-16.4S36.6 8 26 8z"
            fill="#0070D2"
          />
          <path d="M18 24.5h16" stroke="#FFFFFF" strokeWidth="3.2" strokeLinecap="round" />
          <path d="M18 30h11" stroke="#FFFFFF" strokeWidth="3.2" strokeLinecap="round" />
        </chakra.svg>
      </chakra.div>
      Chat with a Live Agent
    </chakra.button>
  );
};

/**
 * Core chat widget component that handles Salesforce integration
 */
const ChatWidgetCore: React.FC<ChatWidgetCoreProps> = ({ partyNumber, displayName, workhours, envVars }) => {
  const {
    NEXT_PUBLIC_MIAW_BUTTON_NAME,
    NEXT_PUBLIC_MIAW_COMM_CASE_RECORD_TYPE,
    NEXT_PUBLIC_MIAW_DEPLOYMENT_ID,
    NEXT_PUBLIC_MIAW_FORCE_URL,
    NEXT_PUBLIC_MIAW_ORGANIZATION_ID,
    NEXT_PUBLIC_MIAW_SCRIPT_SRC_BASE
  } = envVars;

  const [buttonVisible, setButtonVisible] = useState(true);

  const { isAvailable } = useChatSchedule({
    hourOpen: workhours.hourOpen,
    hourClosed: workhours.hourClosed,
    daysClosed: [] // removed in prep for a future update where we remove all scheduling logic
  });

  const { isReady, launchChat } = useSalesforceMessaging({
    isAvailable,
    config: {
      buttonId: NEXT_PUBLIC_MIAW_BUTTON_NAME!,
      commCaseRecordType: NEXT_PUBLIC_MIAW_COMM_CASE_RECORD_TYPE!,
      deploymentId: NEXT_PUBLIC_MIAW_DEPLOYMENT_ID!,
      forceUrl: NEXT_PUBLIC_MIAW_FORCE_URL!,
      organizationId: NEXT_PUBLIC_MIAW_ORGANIZATION_ID!,
      scriptSrcBase: NEXT_PUBLIC_MIAW_SCRIPT_SRC_BASE!
    },
    user: {
      partyNumber,
      displayName
    },
    onMinimize: () => {
      // Show custom launcher on minimize
      setButtonVisible(true);
    },
    onMaximize: () => {
      // Hide custom launcher on maximize
      setButtonVisible(false);
    },
    onClose: () => {
      // Show custom launcher on close
      setButtonVisible(true);
    }
  });

  return (
    <>
      {/* Test identifier */}
      {buttonVisible && isAvailable && isReady && (
        <div data-testid="chat-widget-inner">
          <style
            type="text/css"
            id="liveChatCustomStyles"
            /* Hides the default Salesforce chat button */
            /* eslint-disable-next-line react/no-danger */
            dangerouslySetInnerHTML={{
              __html: `
                .embeddedMessagingConversationButtonWrapper .embeddedMessagingConversationButton {
                  display: none !important;
                }
                .embeddedMessagingFrame.isMinimized {
                  display: none !important;
                }
              `
            }}
          />
          <ChatButton onClick={launchChat} />
        </div>
      )}
    </>
  );
};

type ChatWidgetProps = {
  liveChatOpenHour: number;
  liveChatOpenMinute: number;
  liveChatCloseHour: number;
  liveChatCloseMinute: number;
};

/**
 * Main ChatWidget component that handles data fetching and validation
 * Renders ChatWidgetCore when all required data is available
 */
const LiveChatWidget: React.FC<ChatWidgetProps> = ({
  liveChatOpenHour,
  liveChatOpenMinute,
  liveChatCloseHour,
  liveChatCloseMinute
}) => {
  const partyNumber = usePartyNumber().unwrapOr(null);

  const { data: personalInfo, error: personalInfoError } = trpc.profileAndContactInfo.getPersonalInformation.useQuery(
    undefined,
    {
      enabled: !!partyNumber,
      retry: false
    }
  );

  const envVars = getEnvironmentVariables([
    'NEXT_PUBLIC_MIAW_BUTTON_NAME',
    'NEXT_PUBLIC_MIAW_COMM_CASE_RECORD_TYPE',
    'NEXT_PUBLIC_MIAW_DEPLOYMENT_ID',
    'NEXT_PUBLIC_MIAW_FORCE_URL',
    'NEXT_PUBLIC_MIAW_ORGANIZATION_ID',
    'NEXT_PUBLIC_MIAW_SCRIPT_SRC_BASE'
  ]);

  // Early returns for error cases
  if (personalInfoError) {
    console.error('Error fetching personal information:', personalInfoError);

    return null;
  }

  if (envVars.isErr()) {
    console.info('Error fetching environment variables:', envVars.error);

    return null;
  }

  const displayName = personalInfo?.preferredName ?? personalInfo?.name?.firstName;

  // Convert hours and minutes to decimal format
  const workhoursOpen = liveChatOpenHour + liveChatOpenMinute / 60;
  const workhoursClose = liveChatCloseHour + liveChatCloseMinute / 60;

  // Only render if we have all required data
  if (!partyNumber || !displayName) {
    return null;
  }

  return (
    <ChatWidgetCore
      partyNumber={partyNumber}
      displayName={displayName}
      workhours={{ hourOpen: workhoursOpen, hourClosed: workhoursClose }}
      envVars={envVars.value}
    />
  );
};

export default LiveChatWidget;
